/**
 * Shared Public Navigation Bar
 *
 * Used across all public-facing pages (Landing, How It Works, Cards, FAQ).
 * Shows the brand logo/name on the left, navigation links in the center,
 * and auth actions on the right.
 *
 * Auth actions:
 * - Guests: Sign In / Start for Free
 * - Signed-in users (including free plan): account avatar menu with
 *   "Go to Overview" so the header never implies they need another account
 *
 * Product discovery is grouped under Shop (PBI #1063):
 * - Desktop: rich click/tap Menu (icon + label + descriptor + New badge)
 * - Mobile burger: nested Collapse accordion with the same shared item render.
 *   Shop starts expanded so product links are visible the moment the burger
 *   opens, with no extra tap. It must *start* expanded rather than be expanded
 *   when the burger opens: Mantine's Collapse pins an explicit pixel height
 *   measured from scrollHeight, and the drawer subtree is display:none while
 *   closed, so a nested panel that expands in the same commit measures 0 and
 *   stays collapsed. Collapsing Shop is remembered for the session.
 *
 * On mobile, the navigation links collapse into a burger menu.
 */

import { useContext, useId, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Badge,
  Box,
  Burger,
  Button,
  Collapse,
  Group,
  Menu,
  Stack,
  Text,
  Title,
  UnstyledButton,
  rem,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { brandColors, logoSizes, semanticColors } from '../../config/theme';
import { landingContent } from '../../content/landingContent';
import { AUTH_ROUTES } from '../../constants/auth';
import { PUBLIC_ROUTES } from '../../constants/routes';
import { SIGNUP_CTA_IDS } from '../../constants/signupCtaIds';
import { beginSignup } from '../../services/beginSignup';
import { useAuth } from '../../contexts/AuthContext';
import { visiblePublicGuideNavItems } from '../../constants/publicGuideNav';
import { FeaturesContext } from '../../contexts/FeaturesContext';
import {
  SHOP_NAV_HIGHLIGHT,
  SHOP_NAV_ITEMS,
  isShopNavHighlighted,
  isShopNavPath,
} from '../../constants/shopNav';
import {
  trackShopMenuOpened,
  trackShopProductClicked,
} from '../../services/marketingTracking';
import { ShopNavItemContent } from './ShopNavItemContent';
import { PublicNavAccountMenu } from './PublicNavAccountMenu';

/** Minimum tap target height for mobile Shop children (a11y). */
const MOBILE_SHOP_ITEM_MIN_HEIGHT = 44;

/**
 * Renders the shared public navigation bar with responsive burger menu.
 */
export const PublicNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  // Optional so public pages rendered without FeaturesProvider (unit tests) still mount.
  // Missing context matches the production default: the AI link stays hidden.
  const isPublicAiPageEnabled = useContext(FeaturesContext)?.isPublicAiPageEnabled ?? false;
  const guideNavItems = visiblePublicGuideNavItems(isPublicAiPageEnabled);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [shopMenuOpened, setShopMenuOpened] = useState(false);

  // Expanded from first render so the panel already has an auto height by the
  // time the drawer reveals it — see the nested-Collapse note in the header.
  const [mobileShopOpen, setMobileShopOpen] = useState(true);
  const mobileShopPanelId = useId();

  const shopActive = isShopNavPath(location.pathname);
  const shopHasNew = SHOP_NAV_HIGHLIGHT.highlightKey != null
    && isShopNavHighlighted(SHOP_NAV_HIGHLIGHT.highlightKey);

  // Prefer the account menu over guest CTAs whenever a session exists.
  // Free-plan users are still "logged in" — showing Sign in / Start for Free
  // for them is the confusing state this nav fixes.
  const showAccountMenu = isAuthenticated && !isAuthLoading;
  const showGuestAuthButtons = !isAuthenticated && !isAuthLoading;

  const handleGetStarted = () => {
    beginSignup(navigate, SIGNUP_CTA_IDS.PublicNav);
  };

  const handleSignIn = () => {
    navigate(AUTH_ROUTES.SIGNIN, { replace: true });
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setBurgerOpen(false);
  };

  const handleShopProductClick = (
    path: string,
    productKey: (typeof SHOP_NAV_ITEMS)[number]['key'],
    serviceType: (typeof SHOP_NAV_ITEMS)[number]['serviceType'],
  ) => {
    trackShopProductClicked(productKey, serviceType, 'nav');
    handleNavClick(path);
  };

  const handleDesktopShopChange = (opened: boolean) => {
    setShopMenuOpened(opened);
    if (opened) {
      trackShopMenuOpened('desktop');
    }
  };

  const handleMobileShopToggle = () => {
    setMobileShopOpen((prev) => {
      const next = !prev;
      if (next) {
        trackShopMenuOpened('mobile');
      }
      return next;
    });
  };

  /**
   * Opens or closes the mobile drawer. Shop is expanded by default, so opening
   * the drawer surfaces the products and counts as a Shop impression.
   */
  const handleBurgerToggle = () => {
    setBurgerOpen((wasOpen) => {
      const nextOpen = !wasOpen;
      if (nextOpen && mobileShopOpen) {
        trackShopMenuOpened('mobile');
      }
      return nextOpen;
    });
  };

  const isTopNavLinkActive = (path: string): boolean => {
    if (path === PUBLIC_ROUTES.ai) {
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    }

    return location.pathname === path;
  };

  const renderTopLink = (label: string, path: string, opts?: { py?: string | number }) => {
    const active = isTopNavLinkActive(path);

    return (
      <UnstyledButton key={path} onClick={() => handleNavClick(path)}>
        <Text
          size="sm"
          fw={active ? 600 : 400}
          c={active ? brandColors.indigoText : semanticColors.text.heading}
          style={{
            borderBottom: active
              ? `2px solid var(--mantine-color-indigo-6)`
              : '2px solid transparent',
            paddingBottom: rem(4),
          }}
          py={opts?.py}
        >
          {label}
        </Text>
      </UnstyledButton>
    );
  };

  const renderGuideNavLinks = (opts?: { py?: string | number }) =>
    guideNavItems.map((item) => renderTopLink(item.label, item.path, opts));

  const renderTrailingNavLinks = (opts?: { py?: string | number }) => (
    <>
      {renderGuideNavLinks(opts)}
      {renderTopLink('Pricing', PUBLIC_ROUTES.pricing, opts)}
      {renderTopLink('FAQ', PUBLIC_ROUTES.faq, opts)}
    </>
  );

  return (
    <Box>
      {/* Row 1: Logo + desktop nav links + desktop auth buttons + burger */}
      <Group justify="space-between" align="center" wrap="nowrap">
        {/* Brand logo + name */}
        <UnstyledButton onClick={() => navigate('/')}>
          <Group gap="sm" wrap="nowrap">
            <Box
              component="img"
              src="/favicon.svg"
              alt="Scheduled Smiles"
              style={{ width: logoSizes.medium, height: logoSizes.medium }}
            />
            <Title order={4} c={semanticColors.text.heading} style={{ letterSpacing: -0.3 }}>
              {landingContent.brand.name}
            </Title>
          </Group>
        </UnstyledButton>

        {/* Desktop navigation links */}
        <Group
          gap="lg"
          visibleFrom="sm"
          wrap="nowrap"
          data-testid="public-nav-desktop-links"
        >
          {renderTopLink('How it Works', PUBLIC_ROUTES.howItWorks)}

          <Menu
            opened={shopMenuOpened}
            onChange={handleDesktopShopChange}
            withinPortal
            closeOnClickOutside
            closeOnEscape
            closeOnItemClick
            position="bottom-start"
            offset={8}
            width={320}
          >
            <Menu.Target>
              <UnstyledButton
                aria-haspopup="menu"
                aria-expanded={shopMenuOpened}
                data-testid="public-nav-shop-desktop"
              >
                <Group gap={4} wrap="nowrap">
                  <Text
                    size="sm"
                    fw={shopActive ? 600 : 400}
                    c={shopActive ? brandColors.indigoText : semanticColors.text.heading}
                    style={{
                      borderBottom: shopActive
                        ? `2px solid var(--mantine-color-indigo-6)`
                        : '2px solid transparent',
                      paddingBottom: rem(4),
                    }}
                  >
                    Shop
                  </Text>
                  {shopHasNew && (
                    <Badge size="xs" variant="light" color="grape">
                      {SHOP_NAV_HIGHLIGHT.badgeLabel}
                    </Badge>
                  )}
                  <IconChevronDown size={14} style={{ opacity: 0.7 }} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {SHOP_NAV_ITEMS.map((item) => (
                <Menu.Item
                  key={item.key}
                  onClick={() =>
                    handleShopProductClick(item.path, item.key, item.serviceType)
                  }
                  py="sm"
                >
                  <Box data-testid={`shop-nav-item-${item.key}`}>
                    <ShopNavItemContent item={item} />
                  </Box>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>

          {renderTrailingNavLinks()}
        </Group>

        {/* Desktop guest CTAs; signed-in account menu stays in this row on all sizes */}
        <Group gap="sm" wrap="nowrap">
          {showAccountMenu && <PublicNavAccountMenu />}
          {showGuestAuthButtons && (
            <Group gap="sm" wrap="nowrap" visibleFrom="xs">
              <Button variant="outline" color={brandColors.neutral} onClick={handleSignIn}>
                {landingContent.cta.secondary}
              </Button>
              <Button variant="filled" color={brandColors.primary} onClick={handleGetStarted}>
                {landingContent.cta.primary}
              </Button>
            </Group>
          )}

          {/* Mobile burger toggle */}
          <Burger
            opened={burgerOpen}
            onClick={handleBurgerToggle}
            hiddenFrom="sm"
            size="sm"
            aria-label="Toggle navigation"
            data-testid="public-nav-burger"
          />
        </Group>
      </Group>

      {/* Row 2 (mobile only): guest auth buttons on a subtly lighter strip */}
      {showGuestAuthButtons && (
        <Box
          hiddenFrom="xs"
          mt={rem(8)}
          py={rem(8)}
          data-testid="public-nav-mobile-auth-strip"
          style={{
            background: 'color-mix(in srgb, #D0D9FC 50%, white)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Group gap="sm" justify="center">
            <Button variant="outline" color={brandColors.neutral} onClick={handleSignIn}>
              {landingContent.cta.secondary}
            </Button>
            <Button variant="filled" color={brandColors.primary} onClick={handleGetStarted}>
              {landingContent.cta.primary}
            </Button>
          </Group>
        </Box>
      )}

      {/* Mobile navigation drawer */}
      <Collapse expanded={burgerOpen} hiddenFrom="sm">
        <Stack gap="xs" mt="sm" pb="sm">
          {renderTopLink('How it Works', PUBLIC_ROUTES.howItWorks, { py: rem(6) })}

          {/* Shop accordion — not the desktop Menu (touch-friendly) */}
          <UnstyledButton
            onClick={handleMobileShopToggle}
            aria-expanded={mobileShopOpen}
            aria-controls={mobileShopPanelId}
            data-testid="public-nav-shop-mobile-toggle"
            style={{ minHeight: MOBILE_SHOP_ITEM_MIN_HEIGHT }}
          >
            <Group justify="space-between" wrap="nowrap" py={rem(6)}>
              <Group gap="xs" wrap="nowrap">
                <Text
                  size="sm"
                  fw={shopActive || mobileShopOpen ? 600 : 400}
                  c={
                    shopActive || mobileShopOpen
                      ? semanticColors.text.heading
                      : semanticColors.text.body
                  }
                >
                  Shop
                </Text>
                {shopHasNew && (
                  <Badge size="xs" variant="light" color="grape">
                    {SHOP_NAV_HIGHLIGHT.badgeLabel}
                  </Badge>
                )}
              </Group>
              <IconChevronDown
                size={16}
                style={{
                  transform: mobileShopOpen ? 'rotate(180deg)' : undefined,
                  transition: 'transform 150ms ease',
                }}
              />
            </Group>
          </UnstyledButton>

          <Collapse expanded={mobileShopOpen} id={mobileShopPanelId}>
            <Stack gap={0} pl="sm">
              {SHOP_NAV_ITEMS.map((item) => (
                <UnstyledButton
                  key={item.key}
                  data-testid={`shop-nav-item-mobile-${item.key}`}
                  onClick={() =>
                    handleShopProductClick(item.path, item.key, item.serviceType)
                  }
                  style={{
                    minHeight: MOBILE_SHOP_ITEM_MIN_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  }}
                  py={rem(8)}
                >
                  <ShopNavItemContent item={item} />
                </UnstyledButton>
              ))}
            </Stack>
          </Collapse>

          {renderTrailingNavLinks({ py: rem(6) })}
        </Stack>
      </Collapse>
    </Box>
  );
};
